// 後端 proto SlotResult → 本專案呈現層 SpinResponse 的轉換層。
//
// 後端只給「每步驟盤面 + 彙總數字」（TumbleResult.TumbleSymbol / Win / SymbolWin[]），
// 不直接給「哪些格被消除」；本專案呈現層（TumbleStep）需要 boardBefore/boardAfter/fallMap 等
// 動畫資料，故在此重建：命中格用本地 evaluateWays 重算、fallMap 依命中格推得、贏分用後端權威值。
//
// ⚠️ [ASSUMED] 以下欄位語意皆為推測，尚未經真後端資料驗證，取得後務必校正：
//   1. GameSymbol.reels 的排列：假設 index 0..MAIN_REEL_COUNT-1 為主輪、index TRACKER_REEL_INDEX 為 Tracker 頂輪。
//   2. 長條大符號：假設後端每顆長條在 reel.symbols 內只佔「一筆」CellSymbol（Size>=2），
//      由本層依 Size 展開為「頂格 + (Size-1) 個延續格(size=0)」。若後端改為每列一筆，需改 parseColumn。
//   3. 消除步驟邊界：假設 MGTumbleList / TumbleList 的「最後一筆」為連鎖結束後的最終盤面，
//      其餘每筆的 TumbleSymbol 為該步判定盤面；只對「有本地中獎且有下一筆」的步驟產生消除動畫。
//   4. 命中格：以本地 evaluateWays 規則重算（後端未逐格標記中獎），可能與後端實際消除格有出入。
//   5. Wild 變身：以「下一筆」TumbleResult 的 OriginalSymbol（變身前）vs TumbleSymbol（變身後）差異
//      取得變身格＋原符號（對照 Unity FillSymbol/TransformSymbol 消費補牌目標盤面的差異），
//      歸屬到產生該盤面的消除步驟；TumbleList[0] 若帶 OriginalSymbol（開轉盤面即有變身）暫不呈現。

import {
  FG_START_MULTIPLIER,
  MAIN_REEL_COUNT,
  SCATTER_MAX_POINT,
  SCATTER_SYMBOL_ID,
  scatterLevelFor,
  TRACKER_REEL_INDEX,
  WILD_SYMBOL_ID,
} from '../constants'
import { toClientBalance } from '../../framework/net/balance'
import { evaluateWays } from '../logic/waysEvaluator'
import { fallEntriesFor } from '../logic/tumbleEngine'
import type { Board, BoardCell, ReelColumn } from '../model/board'
import type { FallEntry, TumbleOutcome, TumbleStep, WildTransform } from '../logic/tumbleEngine'
import type { FreeGameOutcome, FreeGameSpinResult, ScatterCollectEvent, SpinResponse } from './SpinService'

// ── 後端 protobufjs decode 物件（keepCase PascalCase；uint64/int64 以 longs:String 解為字串）──
export interface RawCellSymbol {
  Symbol?: number
  Size?: number
}
export interface RawReelSymbol {
  symbols?: RawCellSymbol[] // proto 欄位名為小寫 symbols
}
export interface RawGameSymbol {
  reels?: RawReelSymbol[] // proto 欄位名為小寫 reels
}
export interface RawTumbleResult {
  TumbleSymbol?: RawGameSymbol
  OriginalSymbol?: RawGameSymbol
  SymbolCount?: number[]
  SymbolWays?: number[]
  SymbolWin?: string[]
  Win?: string
  Multiplier?: number
}
export interface RawScatterBonus {
  AddSpinCount?: number
  AddMultiplier?: number
}
export interface RawSpinResult {
  TumbleList?: RawTumbleResult[]
  Strip?: number
  ReelIndex?: number[]
  CumMultiplier?: number
  CumScatterCount?: number
  ScatterBonusList?: RawScatterBonus[]
  SpinWin?: string
}
export interface RawMGResult {
  MGTumbleList?: RawTumbleResult[]
  MGStrip?: number
  MGReelIndex?: number[]
  MainWin?: string
}
export interface RawFGResult {
  FGSpinList?: RawSpinResult[]
  FGSpinCount?: number
  FreeWin?: string
}
export interface RawSlotResult {
  Code?: number
  MGResult?: RawMGResult
  FGResult?: RawFGResult
  TotalWin?: string
  GameMode?: number
  BuyType?: number
}

function toBig(value: string | number | undefined): bigint {
  if (value === undefined || value === '') return 0n
  return typeof value === 'number' ? BigInt(Math.trunc(value)) : BigInt(value)
}

/**
 * 後端單步倍率（TumbleResult.Multiplier，已驗證：Win 為「已乘倍最終分」，前端以 Win÷Multiplier 反推基底分）。
 * 確保回傳 ≥1：未設（undefined/null）、0 或負值皆視為 1，避免顯示端除以倍率時得到 0/Infinity/NaN。
 */
function toMultiplier(value: number | undefined | null): number {
  return typeof value === 'number' && value >= 1 ? value : 1
}

/**
 * 展開後端單一 reel 的 symbols 為固定網格格子：Size>=2 視為長條頂格 + (Size-1) 個延續格(size=0)，
 * 由頂格的 SymbolView 一次涵蓋整顆（對照 Unity ReelStrip：RowIndex += Size、被覆蓋格回傳同一顆）。
 *
 * ⚠️ 後端（實測含 dev 訪客隨機盤面）會對「無長條美術的符號」（H5/H6/H7/LA/LK/LQ/LJ）也給 Size>=2；
 * 此處仍照上述展開（頂格+延續格），維持與後端一致的佔格數與 ways 語意（waysEvaluator 逐格比對）。
 * 缺長條貼圖的視覺 fallback 由 SymbolView 處理（單格貼圖以原生尺寸置中於跨距、背景木框撐成 Size 格高），
 * 對照 Unity SymbolView.SetNormal 的 `NormalSprite{N}x ?? NormalSprite` fallback + SetNativeSize + 木框 sizeDelta.y*Size。
 * 先前若對頂格用單格貼圖卻只畫單格木框，會使符號浮在跨距中心、上下露出盤面木紋而看似「中間空格」。
 */
function parseColumn(symbols: readonly RawCellSymbol[]): BoardCell[] {
  const cells: BoardCell[] = []
  for (const entry of symbols) {
    const symbol = entry.Symbol ?? 0
    const size = entry.Size ?? 1
    if (size >= 2) {
      cells.push({ symbol, size })
      for (let k = 1; k < size; k++) cells.push({ symbol, size: 0 })
    } else {
      cells.push({ symbol })
    }
  }
  return cells
}

/** 後端 GameSymbol → 主盤面(前 MAIN_REEL_COUNT 條) + Tracker 頂輪(第 TRACKER_REEL_INDEX 條)。 */
function parseGameSymbol(raw: RawGameSymbol | null | undefined): { board: Board; tracker: ReelColumn } {
  // protobufjs toObject(defaults:true) 會把未設定的 message 欄位填為 null（非 undefined），故容錯 null。
  const reels = raw?.reels ?? []
  const board: ReelColumn[] = []
  for (let r = 0; r < MAIN_REEL_COUNT; r++) {
    board.push(parseColumn(reels[r]?.symbols ?? []))
  }
  const trackerReel = reels[TRACKER_REEL_INDEX]
  const tracker = trackerReel !== undefined ? parseColumn(trackerReel.symbols ?? []) : []
  return { board, tracker }
}

/** 依命中格推每欄落下路徑（與 tumbleEngine 的補位規則共用同一份 fallEntriesFor）。 */
function fallMapFor(before: Board, winningByReel: ReadonlyMap<number, ReadonlySet<number>>): FallEntry[][] {
  return before.map((column, reelIndex) => fallEntriesFor(column.length, winningByReel.get(reelIndex) ?? new Set<number>()))
}

/**
 * OriginalSymbol(變 Wild 前) → TumbleSymbol(變 Wild 後) 的變身格＋原符號。
 * 對照 Unity GameLogic.DiffTransformSymbolPositions（idBefore != Wild && idAfter == Wild）。
 */
function diffWildTransforms(before: Board, after: Board): WildTransform[] {
  const transforms: WildTransform[] = []
  const reelCount = Math.min(before.length, after.length)
  for (let reel = 0; reel < reelCount; reel++) {
    const b = before[reel]!
    const a = after[reel]!
    const rowCount = Math.min(b.length, a.length)
    for (let row = 0; row < rowCount; row++) {
      if (a[row]!.symbol === WILD_SYMBOL_ID && b[row]!.symbol !== WILD_SYMBOL_ID) {
        transforms.push({ ref: { reel, row }, fromSymbol: b[row]!.symbol })
      }
    }
  }
  return transforms
}

/**
 * 依 OriginalSymbol（變身前盤面）為 Wild 格補上 viewSymbolId（顯示用原符號 id），對照 Unity
 * GameLogic.Trans2BoardData：`cellData.ViewSymbolId = OriginalSymbol[i][j].Symbol`。讓角色符號變身的
 * Wild 顯示 W2/W3/W4 造型而非通用 WW。original 為空（該步無變身）時原board 原樣返回。
 */
function mergeViewSymbols(board: Board, original: Board | null): Board {
  if (original === null) return board
  return board.map((column, reel) =>
    column.map((cell, row) => {
      const orig = original[reel]?.[row]?.symbol
      if (cell.symbol === WILD_SYMBOL_ID && orig !== undefined && orig !== WILD_SYMBOL_ID) {
        return { ...cell, viewSymbolId: orig }
      }
      return cell
    }),
  )
}

function countScatters(board: Board): number {
  return board.reduce(
    (count, column) => count + column.filter((cell) => cell.symbol === SCATTER_SYMBOL_ID).length,
    0,
  )
}

/** 一串後端 TumbleResult → 本專案 TumbleOutcome（重建消除連鎖動畫步驟）。 */
function buildOutcome(tumbleList: readonly RawTumbleResult[]): TumbleOutcome {
  const parsed = tumbleList.map((raw) => {
    const parsedBoard = parseGameSymbol(raw.TumbleSymbol)
    // truthy 檢查同時排除 null(protobufjs 預設值)與 undefined(plain object 省略)。
    const original = raw.OriginalSymbol ? parseGameSymbol(raw.OriginalSymbol).board : null
    return {
      raw,
      // 為 Wild 格補上 viewSymbolId（角色變身造型 W2/W3/W4），symbol 仍為 WILD（不影響 ways/消除判定）。
      board: { board: mergeViewSymbols(parsedBoard.board, original), tracker: parsedBoard.tracker },
      original,
    }
  })

  const steps: TumbleStep[] = []
  let totalWin = 0

  // 最後一筆視為最終盤面，只對「有中獎且有下一筆盤面」的步驟建立消除動畫（見檔頭 [ASSUMED] 3）。
  for (let i = 0; i < parsed.length - 1; i++) {
    const cur = parsed[i]!
    const next = parsed[i + 1]!
    const board = cur.board.board
    const tracker = cur.board.tracker
    const wins = evaluateWays(board, tracker)
    if (wins.length === 0) continue

    const winningByReel = new Map<number, Set<number>>()
    const winningTracker = new Set<number>()
    for (const cell of wins.flatMap((win) => win.cells)) {
      if (cell.reel === TRACKER_REEL_INDEX) {
        winningTracker.add(cell.row)
        continue
      }
      const rows = winningByReel.get(cell.reel) ?? new Set<number>()
      rows.add(cell.row)
      winningByReel.set(cell.reel, rows)
    }

    const stepWin = toClientBalance(toBig(cur.raw.Win))
    // 變身「動畫」歸屬「補牌產生 next 盤面」的這一步（對照 Unity FillSymbol 以 forceOriginalId 掉 next 的
    // OriginalSymbol → TransformSymbol 比對 next 的 Original vs Tumble 差異後變身）。供 ReelsView 演出。
    const wildTransforms = next.original !== null ? diffWildTransforms(next.original, next.board.board) : []
    // newWildCells：本步驟盤面(cur.TumbleSymbol)中「由變身而來的 Wild 格」＝cur 自身 OriginalSymbol vs
    // TumbleSymbol 差異（對照 Unity GameLogic.DiffTransformSymbolPositions，逐一 TumbleResult 判定），純資料用途。
    const newWildCells = cur.original !== null ? diffWildTransforms(cur.original, board).map((t) => t.ref) : []
    steps.push({
      boardBefore: board,
      boardAfter: next.board.board,
      trackerBefore: tracker,
      trackerAfter: next.board.tracker,
      wins,
      win: stepWin,
      multiplier: toMultiplier(cur.raw.Multiplier), // 後端權威倍率（FG 每步套用倍率;主遊戲為 1；已保證 ≥1）。
      wildTransforms,
      newWildCells,
      fallMap: fallMapFor(board, winningByReel),
      // Tracker 頂輪同樣走下沉補位（倖存往畫面左遞補、缺口在最右入口），與主輪 fallMap 同一套規則。
      trackerFall: fallEntriesFor(tracker.length, winningTracker),
    })
    totalWin += stepWin
  }

  const last = parsed[parsed.length - 1]
  return {
    steps,
    totalWin,
    finalBoard: last !== undefined ? last.board.board : [],
    trackerColumn: last !== undefined ? last.board.tracker : [],
  }
}

/**
 * [ASSUMED] 由後端 CumScatterCount（本轉結束累積 Scatter 數）與 ScatterBonusList（本轉發放的獎勵）
 * 重建逐顆 Scatter 計量事件，供計量器/升級/獎勵演出。與 Unity SlotStateMachine.CheckWin 同構
 * （levelUp || count>=maxPoint 時逐筆消費 ScatterBonusList），但未經真後端資料驗證。
 */
function buildScatterEvents(
  prevCumCount: number,
  cumCount: number,
  bonuses: readonly RawScatterBonus[],
): ScatterCollectEvent[] {
  const landCount = Math.max(0, cumCount - prevCumCount)
  const events: ScatterCollectEvent[] = []
  let count = prevCumCount
  let bonusIndex = 0
  for (let i = 0; i < landCount; i++) {
    const before = count
    count += 1
    const level = scatterLevelFor(count)
    const leveledUp = level > scatterLevelFor(before)
    if (leveledUp || count >= SCATTER_MAX_POINT) {
      const raw = bonuses[bonusIndex]
      bonusIndex += 1
      events.push({
        count,
        level,
        leveledUp,
        bonus: { addMultiplier: raw?.AddMultiplier ?? 0, addSpinCount: raw?.AddSpinCount ?? 0 },
      })
    } else {
      events.push({ count, level, leveledUp })
    }
  }
  return events
}

function buildFreeGame(fg: RawFGResult): FreeGameOutcome {
  const spins: FreeGameSpinResult[] = []
  // 後端每轉給 CumMultiplier（本轉結束時的累計倍率）；本轉起始倍率 = 上一轉的結束倍率。
  let prevMultiplier = FG_START_MULTIPLIER
  // Scatter 累積數跨轉推進（CumScatterCount 為本轉結束的累積值）。
  let prevCumScatter = 0
  for (const spinResult of fg.FGSpinList ?? []) {
    const outcome = buildOutcome(spinResult.TumbleList ?? [])
    const startMultiplier = prevMultiplier
    const endMultiplier =
      spinResult.CumMultiplier ?? outcome.steps[outcome.steps.length - 1]?.multiplier ?? startMultiplier
    prevMultiplier = endMultiplier
    const cumScatter = spinResult.CumScatterCount ?? prevCumScatter
    const scatterEvents = buildScatterEvents(prevCumScatter, cumScatter, spinResult.ScatterBonusList ?? [])
    prevCumScatter = cumScatter
    spins.push({
      steps: outcome.steps,
      totalWin: toClientBalance(toBig(spinResult.SpinWin)),
      finalBoard: outcome.finalBoard,
      trackerColumn: outcome.trackerColumn,
      startMultiplier,
      endMultiplier,
      scatterEvents,
    })
  }
  return {
    spins,
    spinCount: fg.FGSpinCount ?? spins.length,
    totalWin: toClientBalance(toBig(fg.FreeWin)),
  }
}

/** 後端 SlotResult → 本專案 SpinResponse。金額一律轉顯示單位（÷10000）。 */
export function mapSlotResultToSpinResponse(raw: RawSlotResult): SpinResponse {
  const mainGame = buildOutcome(raw.MGResult?.MGTumbleList ?? [])
  const fg = raw.FGResult
  const triggeredFreeGame = (fg?.FGSpinList?.length ?? 0) > 0
  const freeGame = triggeredFreeGame && fg ? buildFreeGame(fg) : undefined

  return {
    mainGame,
    // 觸發判斷以 FGResult 是否有內容為準；scatterCount 僅供 UI 顯示（數主遊戲最終盤面的 Scatter）。
    scatterCount: countScatters(mainGame.finalBoard),
    triggeredFreeGame,
    ...(freeGame !== undefined ? { freeGame } : {}),
    totalWin: toClientBalance(toBig(raw.TotalWin)),
  }
}
